// Workout start screen — gateway to a new active session.
//
// Two ways in:
//  1. "Start Empty Workout" — opens a blank session and navigates to /workout/active.
//  2. "Start" on a routine card — pre-populates exercises from that routine.
//
// Both buttons used to be inert HTML. They now drive the activeWorkoutStore
// and route to the session screen.
import type { Exercise, Routine, RoutineExercise, Set as WorkoutSet } from '@onflow/shared';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '../components/v2/ConfirmDialog.js';
import { EmptyState } from '../components/v2/EmptyState.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowCard, FlowLabel } from '../components/v2/FlowCard.js';
import { PageHeader } from '../components/v2/PageHeader.js';
import { useExercises } from '../hooks/useExercises.js';
import { useDeleteRoutine, useMarkRoutineUsed, useRoutines } from '../hooks/useRoutines.js';
import { timeAgo } from '../lib/format.js';
import { toast } from '../lib/toast.js';
import { useActiveWorkoutStore } from '../stores/activeWorkoutStore.js';

type PendingStart = { kind: 'empty' } | { kind: 'routine'; routine: Routine };

function routineToWorkoutExercises(routine: Routine) {
  return routine.exercises.map((re: RoutineExercise) => {
    const sets: WorkoutSet[] = Array.from({ length: re.targetSets }, (_, i) => ({
      setNumber: i + 1,
      type: 'normal',
      weight: re.targetWeight ?? 0,
      reps: re.targetReps ?? 0,
      completed: false,
    }));
    return {
      exerciseId: re.exerciseId,
      notes: '',
      restTimerSeconds: re.restTimerSeconds ?? 90,
      sets,
    };
  });
}

export function Workout() {
  const navigate = useNavigate();
  const routinesQuery = useRoutines();
  const exercises = useExercises();
  const startActive = useActiveWorkoutStore((s) => s.start);
  const activeWorkout = useActiveWorkoutStore((s) => s.workout);
  const markUsed = useMarkRoutineUsed();
  const deleteRoutine = useDeleteRoutine();

  // When a session is already in progress, starting a new one asks first via
  // a single dialog (Resume stays available on the in-progress card).
  const [pending, setPending] = useState<PendingStart | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Routine | null>(null);

  const routines = useMemo(() => routinesQuery.data ?? [], [routinesQuery.data]);

  const exerciseById = useMemo(() => {
    const map = new Map<string, Exercise>();
    for (const ex of exercises.data ?? []) map.set(ex.id, ex);
    return map;
  }, [exercises.data]);

  // Group saved routines by their split folder so a PPL folder's push/pull/legs
  // days read as one split rather than three loose cards. Standalone routines
  // (no folder) trail under a final unlabelled group.
  const routineGroups = useMemo(() => {
    const byFolder = new Map<string, Routine[]>();
    for (const r of routines) {
      const key = r.folder ?? '';
      const list = byFolder.get(key);
      if (list) list.push(r);
      else byFolder.set(key, [r]);
    }
    const named = Array.from(byFolder.keys())
      .filter((k) => k !== '')
      .sort();
    const ordered = [...named, ...(byFolder.has('') ? [''] : [])];
    return ordered.map((folder) => ({ folder, items: byFolder.get(folder)! }));
  }, [routines]);

  const doStartEmpty = () => {
    startActive({ title: 'Workout', exercises: [] });
    navigate('/workout/active');
  };

  const doStartRoutine = (routine: Routine) => {
    startActive({
      title: routine.name,
      exercises: routineToWorkoutExercises(routine),
      routineId: routine.id,
    });
    markUsed.mutate(routine.id);
    navigate('/workout/active');
  };

  const handleStartEmpty = () => {
    if (activeWorkout) {
      setPending({ kind: 'empty' });
      return;
    }
    doStartEmpty();
  };

  const handleStartRoutine = (routine: Routine) => {
    if (activeWorkout) {
      setPending({ kind: 'routine', routine });
      return;
    }
    doStartRoutine(routine);
  };

  const confirmPending = () => {
    if (!pending) return;
    if (pending.kind === 'empty') doStartEmpty();
    else doStartRoutine(pending.routine);
    setPending(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteRoutine.mutateAsync(target.id);
      toast.success('Routine deleted');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Delete failed');
    }
  };

  return (
    <section className="fade-in">
      <PageHeader
        eyebrow="Train"
        title="Start Workout"
        action={
          <FlowButton variant="quiet" size="sm" onClick={() => navigate('/routines')}>
            Manage routines
          </FlowButton>
        }
      />

      {activeWorkout && (
        <FlowCard status="action" glow style={{ marginBottom: 16 }}>
          <FlowLabel tone="action">Session in progress</FlowLabel>
          <div
            style={{
              margin: '6px 0 2px',
              fontFamily: 'var(--font-display)',
              fontSize: 17,
              fontWeight: 700,
              color: 'var(--ink-1)',
            }}
          >
            {activeWorkout.title}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 14 }}>
            Started {timeAgo(activeWorkout.startedAt)} · {activeWorkout.exercises.length} exercise
            {activeWorkout.exercises.length === 1 ? '' : 's'}
          </div>
          <FlowButton variant="primary" block onClick={() => navigate('/workout/active')}>
            Resume workout
          </FlowButton>
        </FlowCard>
      )}

      <FlowButton variant="primary" size="lg" block onClick={handleStartEmpty}>
        Start empty workout
      </FlowButton>

      <FlowLabel style={{ margin: '24px 0 10px' }}>Or start from a saved routine</FlowLabel>

      {routinesQuery.isLoading && <EmptyState title="Loading routines…" />}

      {routinesQuery.error && (
        <EmptyState
          tone="error"
          role="alert"
          title="Couldn't load routines"
          body={(routinesQuery.error as Error).message}
        />
      )}

      {!routinesQuery.isLoading && !routinesQuery.error && routines.length === 0 && (
        <EmptyState
          icon="dumbbell"
          title="No routines yet"
          body="Create a saved routine to launch workouts faster next time."
          action={
            <FlowButton variant="primary" onClick={() => navigate('/routines/new')}>
              Create a routine
            </FlowButton>
          }
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {routineGroups.map(({ folder, items }) => (
          <div key={folder || '__standalone__'}>
            {folder && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-1)' }}>
                  {folder}
                </span>
                <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                  {items.length} day{items.length === 1 ? '' : 's'}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {items.map((r) => {
                // Show the first three exercise names below the routine title.
                const preview = r.exercises
                  .slice(0, 3)
                  .map((re) => exerciseById.get(re.exerciseId)?.name)
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <FlowCard key={r.id} padding="sm">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink-1)' }}>
                          {r.name}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
                          {r.exercises.length} exercise{r.exercises.length === 1 ? '' : 's'}
                          {r.lastUsed ? ` · last used ${timeAgo(r.lastUsed)}` : ' · never used'}
                        </div>
                        {preview && (
                          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>
                            {preview}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <FlowButton variant="soft" size="sm" onClick={() => handleStartRoutine(r)}>
                          Start
                        </FlowButton>
                        <FlowButton
                          variant="quiet"
                          size="sm"
                          onClick={() => navigate(`/routines/edit/${r.id}`)}
                        >
                          Edit
                        </FlowButton>
                        <FlowButton
                          variant="danger"
                          size="sm"
                          disabled={deleteRoutine.isPending}
                          onClick={() => setDeleteTarget(r)}
                        >
                          Delete
                        </FlowButton>
                      </div>
                    </div>
                  </FlowCard>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!pending}
        title="Start a new workout?"
        message={`You have "${activeWorkout?.title ?? 'a workout'}" in progress. Starting a new one discards it. Use Resume above to go back to it instead.`}
        confirmLabel="Discard & start"
        cancelLabel="Keep current"
        confirmVariant="danger"
        onConfirm={confirmPending}
        onCancel={() => setPending(null)}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete routine?"
        message={`"${deleteTarget?.name ?? ''}" will be removed. This can't be undone.`}
        confirmLabel="Delete"
        cancelLabel="Keep"
        confirmVariant="danger"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
}
